import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import useStore from "../../../../useStore";
import Loader from "../../../Elements/Loader/Loader";
import { useToast } from "../../../../Contexts/Toast/toast.provider";
import "./orderByUser.css";
import Button from "../../../Elements/Button/Button";
import { Wallet, initMercadoPago } from "@mercadopago/sdk-react";
import CustomCalendar from "../../../Elements/Calendar/Calendar";
import { fmtCurrency } from "../../../../Utilities/Moneda/moneda";
import { fmtDate } from "../../../../Utilities/Fecha/fecha";
import { buildMapsUrl } from "../../../../Utilities/Maps/BuildUri";
import { badgeClassByPaymentStatus, badgeClassByStatus } from "../../../../Utilities/Status/statusColor";

export default function OrderByUser() {
    const { userId: userIdParam } = useParams();
    const showToast = useToast();

    const {
        myUserOrders,
        loadingMyUserOrders,
        errorMyUserOrders,
        fetchDataOrdersByUser,
        changeStatus,
        changePaymentStatus,
        createPreferencefetch,
        responseCreatePreference
    } = useStore();

    const [activeOrderId, setActiveOrderId] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const lastErrorRef = React.useRef(null);

    const orders = useMemo(() => {
        const arr = Array.isArray(myUserOrders) ? [...myUserOrders] : [];
        return arr.sort((a, b) => (b?.createdAt || "").localeCompare(a?.createdAt || ""));
    }, [myUserOrders]);

    const filteredOrders = useMemo(() => {
        if (!orders || orders.length === 0) return [];

        return orders.filter((order) => {
            if (!order.createdAt) return false;

            const orderDate = new Date(order.createdAt);
            return (
                orderDate.getFullYear() === selectedDate.getFullYear() &&
                orderDate.getMonth() === selectedDate.getMonth() &&
                orderDate.getDate() === selectedDate.getDate()
            );
        });
    }, [orders, selectedDate]);

    const orderDates = useMemo(() => {
        const dates = new Set();
        orders.forEach((o) => {
            if (o.createdAt) {
                const d = new Date(o.createdAt);
                const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                dates.add(key);
            }
        });
        return dates;
    }, [orders]);

    const handlePayOrder = async (order) => {
        try {
            const preferenceBody = {
                orderId: order.orderId,
                restaurante: order.restaurantId,
                items: order.items.map(it => ({
                    id: String(it.id),
                    title: it.title,
                    quantity: it.quantity,
                    unit_price: Number(it.unit_price),
                    currency_id: "MXN"
                })),
                payer: { email: order.clienteEmail || "" },
                metadata: {
                    orderId: order.orderId,           // 👈 necesario para tu webhook
                    restaurante: order.restaurantId,
                    restauranteNombre: order.restauranteNombre,
                    cliente: order.clienteNombre || "Anonimo"
                }
            };

            await createPreferencefetch(preferenceBody);
            setActiveOrderId(order.orderId); // 👈 activa SOLO este pedido
        } catch (e) {
            showToast("error", e?.message || "No se pudo iniciar el pago en Mercado Pago.");
        }
    };

    const handleCancelOrder = useCallback(async (order) => {
        try {
            const res1 = await changeStatus(order.restaurantId, order.orderId, "cancelar");
            if (res1?.statusCode === 200) {
                showToast("success", `Pedido #${order.orderId} cancelado ❌`);
                const res2 = await changePaymentStatus(order.restaurantId, order.orderId, "cancelado");
                if (res2?.statusCode === 200) {
                    showToast("info", `Pago del pedido #${order.orderId} marcado como CANCELADO 💳❌`);
                } else {
                    showToast("warning", `Pedido cancelado, pero el pago no se actualizó.`);
                }
                await fetchDataOrdersByUser(order.userId);
            } else {
                const msg = res1?.message || res1?.body?.message || "No se pudo cancelar el pedido.";
                showToast("error", msg);
            }
        } catch (e) {
            console.error(e);
            showToast("error", e?.message || "Error al cancelar el pedido/pago.");
        }
    }, [changeStatus, changePaymentStatus, fetchDataOrdersByUser, showToast]);

    useEffect(() => {
        const uidFromUrl = decodeURIComponent(userIdParam || "");
        const fallback = (() => {
            try {
                return localStorage.getItem("userId") || "";
            } catch {
                return "";
            }
        })();

        const uid = uidFromUrl || fallback;

        if (uid) {
            fetchDataOrdersByUser(uid);
        }

        // 👇 importantísimo: SOLO dependa del userId de la URL
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userIdParam]);

    useEffect(() => {
        if (!errorMyUserOrders) return;

        if (errorMyUserOrders !== lastErrorRef.current) {
            showToast("error", errorMyUserOrders);
            lastErrorRef.current = errorMyUserOrders;
        }
    }, [errorMyUserOrders, showToast]);

    useEffect(() => {
        const PUBLIC_KEY = process.env.REACT_APP_ML_PUBLIC_KEY;
        if (PUBLIC_KEY) {
            initMercadoPago(PUBLIC_KEY, { locale: "es-MX" });
        }
    }, []);

    useEffect(() => {
        if (orders && orders.length > 0) {
            const interval = setInterval(() => {
                orders.forEach((order) => {
                    if (order.status === "pendiente" && order.paymentMethod === "mercadoPago" && order.paymentStatus === "pendiente") {
                        const created = new Date(order.createdAt).getTime();
                        const now = Date.now();
                        const diff = now - created;
                        const fiveHours = 5 * 60 * 60 * 1000;

                        if (diff > fiveHours) {
                            handleCancelOrder(order);
                        }
                    }
                });
            }, 60000);

            return () => clearInterval(interval);
        }
    }, [orders, handleCancelOrder]);

    return (
        <div className="container-fluid container-mh order-by-user-page py-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="mb-0">📦 Mis pedidos</h2>
            </div>

            <div className="mb-3">
                <CustomCalendar
                    value={selectedDate}
                    onChange={setSelectedDate}
                    orderDates={orderDates}
                />
            </div>

            {loadingMyUserOrders && <Loader />}

            {!loadingMyUserOrders && !errorMyUserOrders && (
                <>
                    {(!filteredOrders || filteredOrders.length === 0) ? (
                        <div className="alert alert-warning text-center">
                            ⚠️ Aún no tienes pedidos realizados.
                        </div>
                    ) : (
                        <div className="row g-3">
                            {filteredOrders.map((o) => {
                                const deliveryUrl = buildMapsUrl(o.delivery);
                                const pickupUrl = buildMapsUrl(o.pickup);

                                return (
                                    <div key={o.orderId} className="col-12 col-md-6 col-lg-4">
                                        <div className="card pr-card order-card h-100 shadow-sm">
                                            <div className="card-body d-flex flex-column">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <h5 className="card-title mb-1">Pedido #{o.orderId}</h5>
                                                    <span className={`badge bg-${badgeClassByStatus(o.status)}`}>{o.status}</span>
                                                </div>
                                                <p className="text-muted mb-2">{fmtDate(o.createdAt)}</p>

                                                <p className="mb-1">
                                                    Restaurante: <strong>{o.restauranteNombre || "-"}</strong>
                                                </p>
                                                <p className="mb-1">
                                                    Método: <strong>{o.paymentMethod || "-"}</strong>
                                                </p>
                                                <p className="mb-1">
                                                    Pago:{" "}
                                                    <span className={`badge bg-${badgeClassByPaymentStatus(o.paymentStatus)}`}>
                                                        {o.paymentStatus || "-"}
                                                    </span>
                                                </p>

                                                <div className="d-flex justify-content-between my-2">
                                                    <small className="text-muted">
                                                        Subtotal: {fmtCurrency(o.subtotal)} • Envío: {fmtCurrency(o.shippingCost)}{" "}
                                                        {o.tip ? <>• Propina: {fmtCurrency(o.tip)}</> : null}
                                                    </small>
                                                    <strong className="text-primary">{fmtCurrency(o.totalAmount)}</strong>
                                                </div>

                                                <h6 className="mt-2">🛒 Artículos</h6>
                                                <ul className="list-unstyled small">
                                                    {(o.items || [])
                                                        .filter((it) => it?.id !== "envio" && it?.id !== "propina")
                                                        .map((it, i) => (
                                                            <li key={i}>
                                                                {it.quantity} × {it.title} — {fmtCurrency(it.unit_price)}
                                                            </li>
                                                        ))}
                                                </ul>

                                                <div className="mt-2">
                                                    <h6 className="mb-1">📍 Entrega</h6>
                                                    <p className="small mb-1">{o?.delivery?.direccion || "-"}</p>
                                                    {deliveryUrl && (
                                                        <a
                                                            href={deliveryUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-outline-primary me-2"
                                                        >
                                                            Ver entrega en Maps
                                                        </a>
                                                    )}
                                                </div>

                                                <div className="mt-3">
                                                    <h6 className="mb-1">🍽️ Recoger (Restaurante)</h6>
                                                    <p className="small mb-1">{o?.pickup?.direccion || "-"}</p>
                                                    {pickupUrl && (
                                                        <a
                                                            href={pickupUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-outline-secondary"
                                                        >
                                                            Ver restaurante en Maps
                                                        </a>
                                                    )}
                                                </div>
                                                {o.status === "pendiente" && (
                                                    <Button
                                                        text="Cancelar pedido"
                                                        color="red"
                                                        fill
                                                        classNames="mt-2"
                                                        action={() => handleCancelOrder(o)}
                                                    />
                                                )}

                                                {(o.status === "pendiente" &&
                                                    o.paymentMethod === "mercadoPago" &&
                                                    o.paymentStatus === "pendiente") && (
                                                        <div className="mt-2">
                                                            <Button
                                                                text="Pagar con Mercado Pago"
                                                                color="blue"
                                                                fill
                                                                classNames="mt-2"
                                                                action={() => handlePayOrder(o)}
                                                                disabled={(() => {
                                                                    const created = new Date(o.createdAt).getTime();
                                                                    const now = Date.now();
                                                                    const diff = now - created;
                                                                    const fiveHours = 5 * 60 * 60 * 1000;
                                                                    return diff > fiveHours;
                                                                })()}
                                                            />

                                                            {activeOrderId === o.orderId && responseCreatePreference?.id && (
                                                                <Wallet
                                                                    initialization={{ preferenceId: responseCreatePreference?.id }}
                                                                    customization={{ texts: { valueProp: "smart_option" } }}
                                                                    locale="es-MX"
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}